import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'

export function Select<T extends string>({
  value,
  onChange,
  options,
  emptyLabel,
  disabled,
  invalid,
}: {
  value: T | ''
  onChange: (v: T | '') => void
  options: { value: T; label: string }[]
  /** Если передан — добавляет опцию с пустым значением */
  emptyLabel?: string
  disabled?: boolean
  /** Подсветить триггер красной нижней границей (валидация). */
  invalid?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [openUpward, setOpenUpward] = useState(false)
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState<number>(220)
  // Координаты dropdown в координатах viewport (для portal). Width равен
  // ширине триггера, top/left — сдвигаем относительно него.
  const [dropdownRect, setDropdownRect] = useState<{
    top: number
    left: number
    width: number
  } | null>(null)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function handlePointer(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        // Если клик в dropdown (вынесен через portal вне ref.current),
        // его onMouseDown сам закроет. Любой клик ВНЕ триггера+dropdown
        // закрывает Select.
        const target = e.target as HTMLElement | null
        if (target && target.closest('.stryzh-select-dropdown')) return
        setOpen(false)
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    function handleReposition() {
      // При скролле / ресайзе пересчитываем позицию dropdown.
      if (ref.current) computeGeometry()
    }
    document.addEventListener('mousedown', handlePointer)
    document.addEventListener('keydown', handleKey)
    window.addEventListener('scroll', handleReposition, true)
    window.addEventListener('resize', handleReposition)
    return () => {
      document.removeEventListener('mousedown', handlePointer)
      document.removeEventListener('keydown', handleKey)
      window.removeEventListener('scroll', handleReposition, true)
      window.removeEventListener('resize', handleReposition)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Вычисление геометрии — координаты + направление открытия. Через portal
  // dropdown не подчиняется overflow родителя (модалки), поэтому считаем
  // место в координатах viewport, а не диалога.
  function computeGeometry() {
    if (!ref.current) return
    const rect = ref.current.getBoundingClientRect()
    const viewportPadding = 12
    const minDropdownHeight = 120
    const preferredDropdownHeight = 260
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding
    const spaceAbove = rect.top - viewportPadding
    const shouldOpenUpward =
      spaceBelow < preferredDropdownHeight && spaceAbove > spaceBelow
    const availableSpace = shouldOpenUpward ? spaceAbove : spaceBelow

    setOpenUpward(shouldOpenUpward)
    setDropdownMaxHeight(Math.max(minDropdownHeight, Math.floor(availableSpace)))
    setDropdownRect({
      top: shouldOpenUpward ? rect.top : rect.bottom,
      left: rect.left,
      width: rect.width,
    })
  }

  useEffect(() => {
    if (!open) return
    computeGeometry()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, options.length])

  const selected = options.find((o) => o.value === value)
  const displayLabel = selected?.label ?? emptyLabel ?? '—'
  const isEmpty = !selected

  const allOptions: { value: T | ''; label: string }[] = emptyLabel
    ? [{ value: '' as T | '', label: emptyLabel }, ...options]
    : options

  return (
    <div ref={ref} className="relative w-full">
      <button
        type="button"
        disabled={disabled}
        aria-invalid={invalid || undefined}
        className={clsx(
          'stryzh-select-trigger',
          open && 'stryzh-select-trigger--open',
          invalid && 'stryzh-select-trigger--error',
        )}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={clsx(isEmpty && 'stryzh-select-trigger__placeholder')}>
          {displayLabel}
        </span>
        <span
          className={clsx(
            'stryzh-select-trigger__chevron',
            open && 'stryzh-select-trigger__chevron--open',
          )}
          aria-hidden
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path
              d="M2 4L6 8L10 4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      {open && dropdownRect
        ? createPortal(
            <div
              className={clsx(
                'stryzh-select-dropdown',
                openUpward && 'stryzh-select-dropdown--upward',
              )}
              style={{
                position: 'fixed',
                top: openUpward ? undefined : `${dropdownRect.top}px`,
                bottom: openUpward
                  ? `${window.innerHeight - dropdownRect.top}px`
                  : undefined,
                left: `${dropdownRect.left}px`,
                width: `${dropdownRect.width}px`,
                maxHeight: `${dropdownMaxHeight}px`,
                zIndex: 1000,
              }}
            >
              {allOptions.map((o) => (
                <button
                  key={o.value === '' ? '__empty__' : o.value}
                  type="button"
                  className={clsx(
                    'stryzh-select-option',
                    value === o.value && 'stryzh-select-option--selected',
                  )}
                  onMouseDown={(e) => {
                    // Prevent focus loss + prevent trigger click toggling
                    e.preventDefault()
                    e.stopPropagation()
                    onChange(o.value as T | '')
                    setOpen(false)
                  }}
                >
                  <span>{o.label}</span>
                  {value === o.value ? (
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      className="stryzh-select-option__check"
                      aria-hidden
                    >
                      <path
                        d="M2.5 7L5.5 10L11.5 4"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : null}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}
